import { formatUsdShort } from '../../hooks/useWalletBalance'

// Whole dollars with up to two decimals: what the backend's BigDecimal amount can carry cleanly.
const AMOUNT_PATTERN = /^\d+(\.\d{1,2})?$/

export type AmountCheck = { ok: true; value: string } | { ok: false; message: string | null }

/**
 * Validates what the user typed against what the wallet holds. `maxUsd` is the backend's figure for
 * the idle tokens in the wallet (money already in DeFi positions is not available); until it is
 * known nothing is accepted.
 * A null message means "nothing to complain about yet" (empty field / still loading).
 */
export function checkAmount(raw: string, maxUsd: number | null): AmountCheck {
  const typed = raw.trim()
  if (!typed) return { ok: false, message: null }
  if (!AMOUNT_PATTERN.test(typed)) return { ok: false, message: 'Enter a number with up to 2 decimals' }
  if (!(Number(typed) > 0)) return { ok: false, message: 'Enter an amount above $0' }
  if (maxUsd == null) return { ok: false, message: null }
  if (Number(typed) > maxUsd) {
    return { ok: false, message: `That is more than your wallet holds (${formatUsdShort(String(maxUsd))})` }
  }
  return { ok: true, value: typed }
}

/** The largest amount that can be typed: rounded down so it never exceeds the balance. */
export function maxAmountString(maxUsd: number): string {
  return (Math.floor(maxUsd * 100) / 100).toFixed(2)
}

interface Props {
  value: string
  onChange: (next: string) => void
  /** Wallet balance the amount may not exceed; null until loaded or when signed out. */
  maxUsd: number | null
  maxLoaded: boolean
  signedIn: boolean
  /** Validation message from checkAmount, if any. */
  message: string | null
  /** Fill the field with the largest allowed amount (the wizard refreshes the cap first). */
  onMax: () => void
}

export function AmountInput({ value, onChange, maxUsd, maxLoaded, signedIn, message, onMax }: Props) {
  const canFill = signedIn && maxUsd != null && maxUsd > 0
  const hint = !signedIn
    ? 'Connect your wallet to see your balance.'
    : !maxLoaded
      ? 'Loading your balance…'
      : maxUsd == null
        ? 'Could not load your balance — reload the page to try again.'
        : maxUsd > 0
          ? `You have ${formatUsdShort(String(maxUsd))} available in your wallet (staked tokens are not counted).`
          : 'Your wallet is empty — add funds to it to continue.'

  return (
    <div className="rounded-xl border border-white/5 bg-surface-card p-4">
      <label htmlFor="earn-amount" className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-2 block">
        How much do you want to put to work? (USD)
      </label>
      <div className="flex gap-2">
        <input
          id="earn-amount"
          name="earn-amount"
          type="text"
          inputMode="decimal"
          autoComplete="off"
          value={value}
          onChange={e => onChange(e.target.value.replace(',', '.'))}
          placeholder="0.00"
          disabled={!canFill}
          aria-invalid={message != null}
          aria-describedby="earn-amount-hint"
          className="flex-1 bg-surface border border-surface-card rounded-lg px-3 py-2 text-sm text-ink placeholder-gray-600 focus:outline-none focus:border-accent-blue disabled:opacity-50"
        />
        <button
          type="button"
          disabled={!canFill}
          onClick={onMax}
          className="px-3 py-2 rounded-lg bg-surface border border-surface-card text-xs text-ink-soft hover:text-ink hover:border-accent-blue disabled:opacity-50 shrink-0"
        >
          Max
        </button>
      </div>
      <p id="earn-amount-hint" className="text-xs text-gray-400 mt-2">{hint}</p>
      {message && <p className="text-accent-red text-xs mt-1" role="alert">{message}</p>}
    </div>
  )
}
